import { useMemo, useState, type FC } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  AlertCircle,
  Calendar,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Clock,
  DollarSign,
  Target,
  TrendingDown,
  TrendingUp,
  Zap,
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useFinanceStore } from '../../store/useFinanceStore';
import type { Goal, GoalAnalytics, GoalHealthStatus } from '../../store/useFinanceStore';
import { normalizeStatus } from '../../utils/transactionMetrics';

interface GoalComparisonChartProps {
  className?: string;
}

type StoreGoal = Goal;

interface DerivedGoalAnalytics extends StoreGoal {
  analytics?: GoalAnalytics;
  resolvedProgress: number;
  resolvedTimeProgress: number;
  daysRemaining: number | null;
  displayMonthlyPace: number;
  displayMonthlyNeeded: number;
  displayGap: number;
  derivedContributionCount: number;
  derivedLastContribution: string | null;
}

interface HealthStyle {
  gradient: string;
  badgeBg: string;
  badgeText: string;
  border: string;
  glow: string;
  scoreText: string;
  titleText: string;
}

const formatCurrency = (value: number) =>
  value
    .toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
    .replace(/\u00a0/g, ' ');

const clampPercent = (value: number) => {
  if (!Number.isFinite(value)) return 0;
  if (value < 0) return 0;
  if (value > 100) return 100;
  return value;
};

const getHealthLabel = (status: GoalHealthStatus) => {
  switch (status) {
    case 'excellent':
      return 'EXCELENTE';
    case 'good':
      return 'BOA';
    case 'attention':
      return 'ATENCAO';
    case 'critical':
      return 'CRITICA';
    default:
      return 'SEM DADOS';
  }
};

const getHealthIcon = (status: GoalHealthStatus) => {
  switch (status) {
    case 'excellent':
      return <CheckCircle2 size={18} />;
    case 'good':
      return <TrendingUp size={18} />;
    case 'attention':
      return <AlertCircle size={18} />;
    case 'critical':
      return <AlertCircle size={18} className="animate-pulse" />;
    default:
      return <Target size={18} />;
  }
};

const getHealthStyles = (status: GoalHealthStatus, isDark: boolean): HealthStyle => {
  const darkPalette: Record<GoalHealthStatus | 'default', HealthStyle> = {
    excellent: {
      gradient: 'from-emerald-900/80 via-emerald-900/40 to-slate-900',
      badgeBg: 'bg-emerald-500/90 backdrop-blur-sm',
      badgeText: 'text-emerald-100',
      border: 'border-emerald-500/40',
      glow: 'shadow-[0_22px_55px_rgba(16,185,129,0.45)]',
      scoreText: 'text-emerald-300',
      titleText: 'text-white',
    },
    good: {
      gradient: 'from-sky-900/70 via-slate-900/60 to-slate-900',
      badgeBg: 'bg-sky-500/90 backdrop-blur-sm',
      badgeText: 'text-sky-100',
      border: 'border-sky-500/40',
      glow: 'shadow-[0_22px_55px_rgba(56,189,248,0.4)]',
      scoreText: 'text-sky-300',
      titleText: 'text-white',
    },
    attention: {
      gradient: 'from-amber-900/70 via-slate-900/70 to-slate-900',
      badgeBg: 'bg-amber-500/90 backdrop-blur-sm',
      badgeText: 'text-amber-100',
      border: 'border-amber-500/40',
      glow: 'shadow-[0_22px_55px_rgba(251,191,36,0.45)]',
      scoreText: 'text-amber-200',
      titleText: 'text-white',
    },
    critical: {
      gradient: 'from-rose-900/80 via-slate-900/70 to-slate-900',
      badgeBg: 'bg-rose-500/90 backdrop-blur-sm',
      badgeText: 'text-rose-100',
      border: 'border-rose-500/50',
      glow: 'shadow-[0_22px_55px_rgba(244,63,94,0.5)]',
      scoreText: 'text-rose-200',
      titleText: 'text-white',
    },
    default: {
      gradient: 'from-slate-800/80 via-slate-900 to-slate-950',
      badgeBg: 'bg-slate-600/90 backdrop-blur-sm',
      badgeText: 'text-slate-100',
      border: 'border-slate-700/40',
      glow: 'shadow-[0_22px_55px_rgba(100,116,139,0.4)]',
      scoreText: 'text-slate-200',
      titleText: 'text-white',
    },
  };

  const lightPalette: Record<GoalHealthStatus | 'default', HealthStyle> = {
    excellent: {
      gradient: 'from-white via-emerald-50 to-teal-100',
      badgeBg: 'bg-emerald-500 text-white',
      badgeText: 'text-white',
      border: 'border-emerald-200',
      glow: 'shadow-[0_22px_55px_rgba(16,185,129,0.25)]',
      scoreText: 'text-emerald-600',
      titleText: 'text-slate-900',
    },
    good: {
      gradient: 'from-white via-sky-50 to-cyan-100',
      badgeBg: 'bg-sky-500 text-white',
      badgeText: 'text-white',
      border: 'border-sky-200',
      glow: 'shadow-[0_22px_55px_rgba(56,189,248,0.25)]',
      scoreText: 'text-sky-600',
      titleText: 'text-slate-900',
    },
    attention: {
      gradient: 'from-white via-amber-50 to-orange-100',
      badgeBg: 'bg-amber-500 text-white',
      badgeText: 'text-white',
      border: 'border-amber-200',
      glow: 'shadow-[0_22px_55px_rgba(251,191,36,0.28)]',
      scoreText: 'text-amber-600',
      titleText: 'text-slate-900',
    },
    critical: {
      gradient: 'from-white via-rose-50 to-rose-100',
      badgeBg: 'bg-rose-500 text-white',
      badgeText: 'text-white',
      border: 'border-rose-200',
      glow: 'shadow-[0_22px_55px_rgba(244,63,94,0.28)]',
      scoreText: 'text-rose-600',
      titleText: 'text-slate-900',
    },
    default: {
      gradient: 'from-white via-slate-50 to-slate-100',
      badgeBg: 'bg-slate-500 text-white',
      badgeText: 'text-white',
      border: 'border-slate-200',
      glow: 'shadow-[0_22px_55px_rgba(148,163,184,0.22)]',
      scoreText: 'text-slate-700',
      titleText: 'text-slate-900',
    },
  };

  const palette = isDark ? darkPalette : lightPalette;
  return palette[status] ?? palette.default;
};

const getFrequencyLabel = (frequency?: GoalAnalytics['contribution_frequency']) => {
  switch (frequency) {
    case 'daily':
      return 'Diaria';
    case 'weekly':
      return 'Semanal';
    case 'monthly':
      return 'Mensal';
    case 'sporadic':
      return 'Esporadica';
    case 'none':
      return 'Sem contribuicoes';
    default:
      return 'Nao informado';
  }
};

const getDeadlineCopy = (daysRemaining: number | null) => {
  if (daysRemaining == null) return 'Prazo nao informado';
  if (daysRemaining < 0) return `Vencida ha ${Math.abs(daysRemaining)} dias`;
  if (daysRemaining === 0) return 'Vence hoje';
  if (daysRemaining === 1) return 'Falta 1 dia';
  if (daysRemaining <= 7) return `Faltam ${daysRemaining} dias`;
  return `Prazo em ${daysRemaining} dias`;
};

const getDeadlineTone = (daysRemaining: number | null, isDark: boolean) => {
  if (daysRemaining == null) return isDark ? 'text-slate-400' : 'text-slate-600';
  if (daysRemaining < 0) return isDark ? 'text-rose-300' : 'text-rose-600';
  if (daysRemaining <= 7) return isDark ? 'text-amber-300' : 'text-amber-600';
  return isDark ? 'text-slate-200' : 'text-slate-600';
};

const normalizeText = (value?: string | null) =>
  value ? value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase() : '';

const calculateLocalGoalMath = (
  goal: Goal,
  analytics: GoalAnalytics | undefined,
  transactions: ReturnType<typeof useFinanceStore>['transactions']
) => {
  const now = new Date();
  const thirtyDaysAgo = new Date(now.getTime() - 1000 * 60 * 60 * 24 * 30);
  const normalizedGoalName = normalizeText(goal.name);

  const goalTransactions = transactions.filter((tx) => {
    const status = normalizeStatus(tx, now);
    if (status === 'cancelled') return false;

    const descriptionMatch = normalizeText(tx.description).includes(normalizedGoalName);
    const notesMatch = normalizeText(tx.notes).includes(normalizedGoalName);
    const categoryMatch = normalizeText(tx.category) === 'aporte da meta';
    return descriptionMatch || notesMatch || categoryMatch;
  });

  const contributions = goalTransactions.filter((tx) => tx.amount !== 0);
  const contributionCount = contributions.length;
  const lastContribution = contributions
    .map((tx) => new Date(tx.date))
    .filter((date) => !Number.isNaN(date.getTime()))
    .sort((a, b) => b.getTime() - a.getTime())[0];

  const monthlyPaceLocal = contributions
    .filter((tx) => {
      const txDate = new Date(tx.date);
      return !Number.isNaN(txDate.getTime()) && txDate >= thirtyDaysAgo;
    })
    .reduce((sum, tx) => sum + Math.abs(tx.amount), 0);

  const amountRemaining = Math.max(goal.targetAmount - goal.currentAmount, 0);
  const deadline = goal.deadline ? new Date(goal.deadline) : null;
  const daysRemainingFromGoal = deadline && !Number.isNaN(deadline.getTime())
    ? Math.ceil((deadline.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
    : null;
  const monthsRemaining = daysRemainingFromGoal != null && daysRemainingFromGoal > 0
    ? Math.max(1, Math.ceil(daysRemainingFromGoal / 30))
    : amountRemaining > 0
      ? 1
      : 0;

  const monthlyNeededLocal = amountRemaining > 0 && monthsRemaining > 0
    ? amountRemaining / monthsRemaining
    : 0;

  const paceCandidate = Number.isFinite(analytics?.current_monthly_pace)
    ? analytics!.current_monthly_pace
    : 0;
  const safeUpperBound = Math.max(goal.targetAmount, goal.currentAmount + amountRemaining);
  const resolvedMonthlyPace = monthlyPaceLocal > 0
    ? monthlyPaceLocal
    : Math.min(Math.max(paceCandidate, 0), safeUpperBound);

  const resolvedMonthlyNeeded = monthlyNeededLocal;

  return {
    monthlyPace: resolvedMonthlyPace,
    monthlyNeeded: resolvedMonthlyNeeded,
    gap: resolvedMonthlyPace - resolvedMonthlyNeeded,
    contributionCount,
    lastContribution: lastContribution ? lastContribution.toISOString() : null,
  };
};

const GoalComparisonChart: FC<GoalComparisonChartProps> = ({ className = '' }) => {
  const goals = useFinanceStore((state) => state.goals);
  const goalAnalytics = useFinanceStore((state) => state.goalAnalytics);
  const transactions = useFinanceStore((state) => state.transactions);
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [showDeepAnalysis, setShowDeepAnalysis] = useState(false);

  const derivedGoals = useMemo<DerivedGoalAnalytics[]>(() => {
    const now = Date.now();
    const dayMs = 1000 * 60 * 60 * 24;

    return goals
      .map((goal) => {
        const analytics = goalAnalytics.find((entry) => entry.goal_id === goal.id);
        const localMath = calculateLocalGoalMath(goal, analytics, transactions);
        const rawProgress =
          analytics?.progress_percentage ?? (goal.targetAmount > 0 ? (goal.currentAmount / goal.targetAmount) * 100 : 0);

        let daysRemaining: number | null = null;
        if (analytics) {
          daysRemaining = analytics.days_remaining;
        } else if (goal.deadline) {
          const deadlineDate = new Date(goal.deadline);
          if (!Number.isNaN(deadlineDate.getTime())) {
            daysRemaining = Math.ceil((deadlineDate.getTime() - now) / dayMs);
          }
        }

        const rawTimeProgress = analytics
          ? analytics.time_progress_percentage ??
            (analytics.days_total > 0
              ? ((analytics.days_total - analytics.days_remaining) / analytics.days_total) * 100
              : 0)
          : 0;

        return {
          ...goal,
          analytics,
          resolvedProgress: clampPercent(rawProgress),
          resolvedTimeProgress: clampPercent(rawTimeProgress),
          daysRemaining,
          displayMonthlyPace: localMath.monthlyPace,
          displayMonthlyNeeded: localMath.monthlyNeeded,
          displayGap: localMath.gap,
          derivedContributionCount: localMath.contributionCount,
          derivedLastContribution: localMath.lastContribution,
        };
      })
      .sort((a, b) => {
        const aScore = a.analytics?.health_score ?? 101;
        const bScore = b.analytics?.health_score ?? 101;
        return aScore - bScore;
      });
    }, [goals, goalAnalytics, transactions]);

  const summary = useMemo(() => {
    if (!derivedGoals.length) {
      return {
        totalGoals: 0,
        totalSaved: 0,
        totalTarget: 0,
        avgProgress: 0,
        onTrack: 0,
        needsAttention: 0,
        critical: 0,
        attention: 0,
        completed: 0,
        upcoming: [] as DerivedGoalAnalytics[],
      };
    }

    const totals = derivedGoals.reduce(
      (acc, goal) => {
        acc.totalSaved += goal.currentAmount;
        acc.totalTarget += goal.targetAmount;
        acc.sumProgress += goal.resolvedProgress;

        if (goal.analytics?.will_complete_on_time) acc.onTrack += 1;
        if (goal.analytics?.needs_attention) acc.needsAttention += 1;
        if (goal.analytics?.status_health === 'critical') acc.critical += 1;
        if (goal.analytics?.status_health === 'attention') acc.attention += 1;
        if (goal.resolvedProgress >= 100) acc.completed += 1;
        return acc;
      },
      {
        totalSaved: 0,
        totalTarget: 0,
        sumProgress: 0,
        onTrack: 0,
        needsAttention: 0,
        critical: 0,
        attention: 0,
        completed: 0,
      }
    );

    const upcoming = [...derivedGoals]
      .filter((goal) => typeof goal.daysRemaining === 'number')
      .sort((a, b) => (a.daysRemaining ?? 9999) - (b.daysRemaining ?? 9999))
      .slice(0, 3);

    return {
      totalGoals: derivedGoals.length,
      totalSaved: totals.totalSaved,
      totalTarget: totals.totalTarget,
      avgProgress: totals.sumProgress / derivedGoals.length,
      onTrack: totals.onTrack,
      needsAttention: totals.needsAttention,
      critical: totals.critical,
      attention: totals.attention,
      completed: totals.completed,
      upcoming,
    };
  }, [derivedGoals]);

  const containerClass = `relative overflow-hidden rounded-3xl border p-6 transition-colors ${
    isDark
      ? 'bg-slate-950/95 border-slate-800 shadow-[0_24px_60px_rgba(15,23,42,0.45)]'
      : 'bg-white border-teal-200 shadow-[0_24px_60px_rgba(45,212,191,0.18)]'
  }`;

  const summaryCardClass = `rounded-2xl border p-4 transition-colors ${
    isDark
      ? 'bg-slate-950/80 border-slate-800/80 hover:border-slate-700'
      : 'bg-gradient-to-br from-white via-teal-50 to-white border-teal-100 hover:border-teal-200 shadow-[0_12px_30px_rgba(94,234,212,0.18)]'
  }`;

  const secondaryPanelClass = `rounded-2xl border p-3 transition-colors ${
    isDark
      ? 'bg-slate-900/70 border-slate-800/70'
      : 'bg-white/90 border-teal-100 shadow-[0_12px_30px_rgba(94,234,212,0.2)]'
  }`;

  if (!derivedGoals.length) {
    return (
      <div className={`${containerClass} ${className}`}>
        <div className="relative flex flex-col items-center justify-center gap-3 py-16 text-center">
          <div
            className={`flex h-12 w-12 items-center justify-center rounded-2xl ${
              isDark ? 'bg-emerald-500/20 text-emerald-300' : 'bg-teal-100 text-teal-600'
            }`}
          >
            <Target size={26} />
          </div>
          <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
            Nenhuma meta cadastrada ainda
          </h3>
          <p className={`max-w-sm text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
            Adicione suas metas financeiras para acompanhar progresso, ritmo e recomendacoes inteligentes.
          </p>
        </div>
      </div>
    );
  }

  const summaryCards = [
    {
      title: 'Metas ativas',
      value: summary.totalGoals,
      subtitle: summary.completed ? `${summary.completed} concluidas` : 'Em andamento',
      icon: Target,
    },
    {
      title: 'Total poupado',
      value: formatCurrency(summary.totalSaved),
      subtitle: `de ${formatCurrency(summary.totalTarget)}`,
      icon: DollarSign,
    },
    {
      title: 'Progresso medio',
      value: `${summary.avgProgress.toFixed(1)}%`,
      subtitle: `${summary.onTrack} no prazo`,
      icon: TrendingUp,
    },
    {
      title: 'Precisam atencao',
      value: summary.needsAttention,
      subtitle: `${summary.critical} criticas - ${summary.attention} atencao`,
      icon: AlertCircle,
    },
  ];

  return (
    <div className={`${containerClass} ${className}`}>
      <div
        className={`pointer-events-none absolute inset-0 opacity-5 ${
          isDark
            ? 'bg-[radial-gradient(circle_at_top,_rgba(16,185,129,0.4),transparent_55%)]'
            : 'bg-[radial-gradient(circle_at_top,_rgba(45,212,191,0.35),transparent_60%)]'
        }`}
      />

      <div className="relative z-10">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div
              className={`flex h-12 w-12 items-center justify-center rounded-2xl ${
                isDark
                  ? 'bg-gradient-to-br from-emerald-500 to-sky-500 text-white shadow-[0_18px_45px_rgba(16,185,129,0.35)]'
                  : 'bg-gradient-to-br from-teal-500 to-emerald-500 text-white shadow-[0_18px_45px_rgba(45,212,191,0.35)]'
              }`}
            >
              <TrendingUp size={24} />
            </div>
            <div>
              <h3 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Analise Comparativa das Metas
              </h3>
              <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                Visualize ritmo, prazo e recomendacoes personalizadas
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={() => setShowDeepAnalysis((prev) => !prev)}
            className={`flex items-center gap-2 rounded-2xl px-4 py-2 text-sm font-semibold transition-all ${
              isDark
                ? 'bg-slate-900/80 border border-slate-700 text-slate-100 hover:bg-slate-900'
                : 'bg-white border border-teal-200 text-teal-600 shadow-[0_12px_30px_rgba(94,234,212,0.2)] hover:bg-teal-50'
            }`}
          >
            {showDeepAnalysis ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            Analise profunda
          </button>
        </div>

        <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {summaryCards.map(({ title, value, subtitle, icon: Icon }) => (
            <div key={title} className={summaryCardClass}>
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p
                    className={`text-xs font-semibold uppercase tracking-wider ${
                      isDark ? 'text-slate-400' : 'text-teal-600'
                    }`}
                  >
                    {title}
                  </p>
                  <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>{value}</p>
                  <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>{subtitle}</p>
                </div>
                <div
                  className={`flex h-10 w-10 items-center justify-center rounded-xl ${
                    isDark ? 'bg-slate-900/90 text-slate-200' : 'bg-teal-100 text-teal-600'
                  }`}
                >
                  <Icon size={20} />
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <AnimatePresence>
            {derivedGoals.map((goal, index) => {
              const analytics = goal.analytics;
              const status = analytics?.status_health ?? 'good';
              const style = getHealthStyles(status, isDark);
              const gapValue = goal.displayGap;
              const chipTone =
                gapValue >= 0
                  ? isDark
                    ? 'text-emerald-300'
                    : 'text-emerald-600'
                  : isDark
                    ? 'text-rose-300'
                    : 'text-rose-600';

              const progressBarClass = goal.resolvedProgress >= goal.resolvedTimeProgress
                ? isDark
                  ? 'bg-gradient-to-r from-emerald-400 to-teal-400'
                  : 'bg-gradient-to-r from-emerald-500 to-teal-400'
                : isDark
                  ? 'bg-gradient-to-r from-amber-400 to-orange-400'
                  : 'bg-gradient-to-r from-amber-400 to-orange-300';

              const insights = (() => {
                if (!analytics) {
                  return ['Registre contribuicoes para liberar recomendacoes personalizadas.'];
                }

                const suggestions = new Set<string>();
                if (analytics.alert_message) suggestions.add(analytics.alert_message);
                if (analytics.recommended_action) suggestions.add(analytics.recommended_action);

                if (analytics.days_remaining < 0) suggestions.add('Meta atrasada - priorize recursos.');
                else if (analytics.days_remaining <= 7) suggestions.add(`Prazo muito proximo: ${analytics.days_remaining} dias.`);
                else if (analytics.days_remaining <= 30) suggestions.add(`Plano dentro de ${analytics.days_remaining} dias.`);

                if (!analytics.will_complete_on_time && goal.displayMonthlyPace > 0) {
                  suggestions.add('Reforce o aporte mensal para atingir a meta no prazo.');
                }

                if (analytics.total_contributions === 0) suggestions.add('Sem contribuicoes registradas ate o momento.');
                if (analytics.contribution_frequency === 'sporadic') {
                  suggestions.add('Contribuicoes irregulares indicam risco no cronograma.');
                }

                return Array.from(suggestions).slice(0, 4);
              })();

              return (
                <motion.div
                  key={goal.id}
                  initial={{ opacity: 0, y: 16, scale: 0.96 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -12 }}
                  transition={{ duration: 0.35, delay: index * 0.08 }}
                  className={`relative overflow-hidden rounded-3xl border bg-gradient-to-br p-5 transition-transform ${
                    style.gradient
                  } ${style.border} ${style.glow}`}
                >
                  <div className="pointer-events-none absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0iI0ZGRiIgc3Ryb2tlLXdpZHRoPSIwLjEiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')] opacity-10" />

                  <div className="relative z-10 flex flex-col gap-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0 space-y-2">
                        <div className="flex flex-wrap items-center gap-2">
                          <span
                            className={`flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-widest ${style.badgeBg} ${style.badgeText}`}
                          >
                            {getHealthIcon(status)}
                            {getHealthLabel(status)}
                          </span>
                          {analytics?.health_score != null && (
                            <span
                              className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold ${
                                isDark ? 'border-slate-700/70 text-slate-200' : 'border-slate-200 text-slate-600'
                              }`}
                            >
                              Score {analytics.health_score.toFixed(0)}
                            </span>
                          )}
                        </div>
                        <h4 className={`truncate text-lg font-bold ${style.titleText}`}>{goal.name}</h4>
                      </div>

                      <div className="text-right">
                        <p
                          className={`text-xs font-semibold uppercase tracking-[0.35em] ${
                            isDark ? 'text-slate-400/90' : 'text-slate-500/90'
                          }`}
                        >
                          ritmo
                        </p>
                        <p className={`text-4xl font-black leading-none ${style.scoreText}`}>
                          {goal.displayMonthlyPace
                            ? formatCurrency(goal.displayMonthlyPace).replace('R$ ', '')
                            : '--'}
                        </p>
                      </div>
                    </div>

                    <div className="grid gap-4 lg:grid-cols-2">
                      <div className="space-y-4">
                        <div>
                          <div className={`flex flex-wrap items-baseline gap-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                            <span className="text-2xl font-bold">{formatCurrency(goal.currentAmount)}</span>
                            <span className={`text-sm font-medium ${isDark ? 'text-slate-300/90' : 'text-slate-500'}`}>
                              de {formatCurrency(goal.targetAmount)}
                            </span>
                          </div>
                          <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                            {analytics?.total_contributions ?? 0} contribuicoes registradas
                          </p>
                        </div>

                        <div className="space-y-3">
                          <div>
                            <div className={`mb-1 flex items-center justify-between text-xs font-semibold ${
                              isDark ? 'text-slate-300' : 'text-slate-600'
                            }`}>
                              <span>Progresso financeiro</span>
                              <span>{goal.resolvedProgress.toFixed(1)}%</span>
                            </div>
                            <div
                              className={`h-2.5 overflow-hidden rounded-full border ${
                                isDark ? 'border-slate-800 bg-slate-950' : 'border-teal-100 bg-white'
                              }`}
                            >
                              <motion.div
                                initial={{ width: 0 }}
                                animate={{ width: `${goal.resolvedProgress}%` }}
                                transition={{ duration: 0.8, delay: index * 0.08, ease: 'easeOut' }}
                                className={`h-full rounded-full ${progressBarClass}`}
                              />
                            </div>
                          </div>

                          <div>
                            <div className={`mb-1 flex items-center justify-between text-xs font-semibold ${
                              isDark ? 'text-slate-300' : 'text-slate-600'
                            }`}>
                              <span>Progresso do tempo</span>
                              <span>{goal.resolvedTimeProgress.toFixed(1)}%</span>
                            </div>
                            <div
                              className={`h-2.5 overflow-hidden rounded-full border ${
                                isDark ? 'border-slate-800 bg-slate-950' : 'border-teal-100 bg-white'
                              }`}
                            >
                              <motion.div
                                initial={{ width: 0 }}
                                animate={{ width: `${goal.resolvedTimeProgress}%` }}
                                transition={{ duration: 0.8, delay: index * 0.1, ease: 'easeOut' }}
                                className={`h-full rounded-full ${isDark ? 'bg-slate-500/80' : 'bg-teal-300/80'}`}
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="space-y-3">
                        <div className={secondaryPanelClass}>
                          <div className="flex items-center justify-between text-xs font-semibold">
                            <span className={isDark ? 'text-slate-300' : 'text-slate-600'}>Atual / mes</span>
                            <span className={isDark ? 'text-white' : 'text-slate-900'}>
                              {formatCurrency(goal.displayMonthlyPace)}
                            </span>
                          </div>
                          <div className="flex items-center justify-between text-xs">
                            <span className={isDark ? 'text-slate-400' : 'text-slate-600'}>Necessario</span>
                            <span className={isDark ? 'text-slate-200' : 'text-slate-700'}>
                              {formatCurrency(goal.displayMonthlyNeeded)}
                            </span>
                          </div>
                          <div className="flex items-center justify-between text-xs">
                            <span className={isDark ? 'text-slate-400' : 'text-slate-600'}>Gap mensal</span>
                            <span className={`font-semibold ${chipTone}`}>
                              {formatCurrency(goal.displayGap)}
                            </span>
                          </div>
                        </div>

                        <div className={secondaryPanelClass}>
                          <div className={`mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide ${
                            isDark ? 'text-slate-300' : 'text-slate-600'
                          }`}>
                            <Clock size={14} />
                            Status do prazo
                          </div>
                          <div className={`flex items-center gap-3 text-sm ${getDeadlineTone(goal.daysRemaining, isDark)}`}>
                            <Calendar size={16} />
                            <span>{getDeadlineCopy(goal.daysRemaining)}</span>
                          </div>
                          <div className={`mt-2 flex items-center gap-2 text-xs ${
                            isDark ? 'text-slate-400' : 'text-slate-600'
                          }`}>
                            <Zap size={14} />
                            <span>Frequencia: {getFrequencyLabel(analytics?.contribution_frequency)}</span>
                          </div>
                        </div>

                        {insights.length > 0 && (
                          <div className={secondaryPanelClass}>
                            <p className={`mb-2 text-xs font-semibold uppercase tracking-wide ${
                              isDark ? 'text-slate-300' : 'text-slate-600'
                            }`}>
                              Insights rapidos
                            </p>
                            <ul className="space-y-1.5 text-xs">
                              {insights.map((insight) => (
                                <li key={insight} className={isDark ? 'text-slate-300' : 'text-slate-600'}>
                                  <span className={isDark ? 'text-emerald-300' : 'text-teal-600'}>- </span>
                                  {insight}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>

        <AnimatePresence>
          {showDeepAnalysis && (
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.3 }}
              className="mt-6 grid gap-4 lg:grid-cols-2"
            >
              <div
                className={`rounded-2xl border p-4 transition-colors ${
                  isDark
                    ? 'bg-gradient-to-br from-indigo-950/40 via-slate-950/60 to-slate-950 border-indigo-900/50'
                    : 'bg-gradient-to-br from-white via-teal-50 to-emerald-50 border-teal-200 shadow-[0_18px_45px_rgba(94,234,212,0.22)]'
                }`}
              >
                <h4 className={`mb-3 text-sm font-semibold uppercase tracking-wide ${
                  isDark ? 'text-slate-200' : 'text-teal-700'
                }`}>
                  Proximas prioridades
                </h4>
                <div className="space-y-3">
                  {summary.upcoming.length === 0 && (
                    <p className={isDark ? 'text-slate-400' : 'text-slate-600'}>
                      Nenhum prazo curto detectado - bom momento para planejar novas metas.
                    </p>
                  )}
                  {summary.upcoming.map((goal) => (
                    <div
                      key={goal.id}
                      className={`flex items-center justify-between rounded-2xl border px-3 py-2 ${
                        isDark ? 'border-slate-800 bg-slate-950/80' : 'border-teal-100 bg-white/95'
                      }`}
                    >
                      <div className="min-w-0">
                        <p className={`truncate text-sm font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                          {goal.name}
                        </p>
                        <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                          {goal.daysRemaining != null
                            ? goal.daysRemaining >= 0
                              ? `${goal.daysRemaining} dias restantes`
                              : `Atrasada ${Math.abs(goal.daysRemaining)} dias`
                            : 'Sem prazo definido'}
                        </p>
                      </div>
                      <span
                        className={`text-sm font-semibold ${
                          getHealthStyles(goal.analytics?.status_health ?? 'good', isDark).scoreText
                        }`}
                      >
                        {goal.resolvedProgress.toFixed(0)}%
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div
                className={`rounded-2xl border p-4 transition-colors ${
                  isDark
                    ? 'bg-gradient-to-br from-indigo-950/40 via-slate-950/60 to-slate-950 border-indigo-900/50'
                    : 'bg-gradient-to-br from-white via-teal-50 to-emerald-50 border-teal-200 shadow-[0_18px_45px_rgba(94,234,212,0.22)]'
                }`}
              >
                <h4 className={`mb-3 text-sm font-semibold uppercase tracking-wide ${
                  isDark ? 'text-slate-200' : 'text-teal-700'
                }`}>
                  Diagnostico geral
                </h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className={`flex flex-col gap-1 rounded-2xl border p-3 ${
                    isDark ? 'border-slate-800 bg-slate-950/80' : 'border-teal-100 bg-white/95'
                  }`}>
                    <span className={isDark ? 'text-slate-400' : 'text-slate-600'}>No prazo</span>
                    <span className={`text-xl font-bold ${isDark ? 'text-emerald-300' : 'text-emerald-600'}`}>
                      {summary.onTrack}
                    </span>
                  </div>
                  <div className={`flex flex-col gap-1 rounded-2xl border p-3 ${
                    isDark ? 'border-slate-800 bg-slate-950/80' : 'border-teal-100 bg-white/95'
                  }`}>
                    <span className={isDark ? 'text-slate-400' : 'text-slate-600'}>Em atencao</span>
                    <span className={`text-xl font-bold ${isDark ? 'text-amber-300' : 'text-amber-600'}`}>
                      {summary.attention}
                    </span>
                  </div>
                  <div className={`flex flex-col gap-1 rounded-2xl border p-3 ${
                    isDark ? 'border-slate-800 bg-slate-950/80' : 'border-teal-100 bg-white/95'
                  }`}>
                    <span className={isDark ? 'text-slate-400' : 'text-slate-600'}>Criticas</span>
                    <span className={`text-xl font-bold ${isDark ? 'text-rose-300' : 'text-rose-600'}`}>
                      {summary.critical}
                    </span>
                  </div>
                  <div className={`flex flex-col gap-1 rounded-2xl border p-3 ${
                    isDark ? 'border-slate-800 bg-slate-950/80' : 'border-teal-100 bg-white/95'
                  }`}>
                    <span className={isDark ? 'text-slate-400' : 'text-slate-600'}>Concluidas</span>
                    <span className={`text-xl font-bold ${isDark ? 'text-emerald-300' : 'text-emerald-600'}`}>
                      {summary.completed}
                    </span>
                  </div>
                </div>

                <div className={`mt-4 flex items-center gap-3 rounded-2xl border p-3 text-sm ${
                  isDark
                    ? 'border-slate-800 bg-slate-950/80 text-slate-300'
                    : 'border-teal-100 bg-white/95 text-slate-600'
                }`}>
                  <TrendingDown size={18} className={isDark ? 'text-rose-300' : 'text-rose-500'} />
                  <span>
                    {summary.needsAttention
                      ? 'Algumas metas exigem reforco imediato nos aportes. Revise o plano de contribuicoes.'
                      : 'Todas as metas estao seguindo o ritmo esperado. Continue monitorando!'}
                  </span>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default GoalComparisonChart;